import React, { useEffect, useRef, useState } from 'react';
import usePageTitle from '../../hooks/usePageTitle';

const CHATASSIST_URL = 'https://cerebralmachines.ai/chatassist/6c1d2e2c-dde3-49c7-8d2c-65083218898b';

/**
 * AskCmPage -- embeds the existing CerebralMachines "Test Graph" chat
 * (live at https://cerebralmachines.ai/user-home/graphs/build/<id>/train-test/test-graph)
 * inside the DM SPA. No new chat code is shipped; this is purely the
 * existing chat exposed under the DM nav so users can ask any denial /
 * coding / policy / payer question without leaving the product.
 *
 * The host CM site allows dm.cerebralmachines.ai as a frame-ancestor in
 * its CSP (see deploy/nginx.prod.conf). The iframe runs at CM's origin,
 * so authentication and API calls follow the user's CM session inside
 * the frame. A "Open in new tab" fallback is always offered in case the
 * iframe is blocked (corporate proxies, older browsers, etc.).
 */
export default function AskCmPage() {
  usePageTitle('Ask CM');

  const iframeRef = useRef(null);
  const [src, setSrc] = useState(CHATASSIST_URL);
  const [blocked, setBlocked] = useState(false);
  // The "open in new tab" link uses the same chrome-less embed URL so
  // the user never sees the CM login or brand even outside the iframe.
  const openHref = src;

  useEffect(() => {
    // If the iframe has not signalled a load within 8s we assume the browser
    // refused to embed it (X-Frame-Options / CSP / corporate filter) and we
    // expose the fallback prominently.
    const t = setTimeout(() => {
      const f = iframeRef.current;
      if (!f) return;
      try {
        if (!f.contentWindow || !f.contentWindow.location) setBlocked(true);
      } catch (_) {
        // Cross-origin reads will throw -- that is the success signal, not a
        // failure (the iframe loaded and the browser is enforcing origin
        // isolation). Do nothing.
      }
    }, 8000);
    return () => clearTimeout(t);
  }, [src]);

  function reload() {
    setBlocked(false);
    const sep = CHATASSIST_URL.includes('?') ? '&' : '?';
    setSrc(`${CHATASSIST_URL}${sep}t=${Date.now()}`);
  }

  return (
    <div className="dm-page dm-askcm">
      <header className="dm-page__header">
        <h1 className="dm-page__title">Ask CM</h1>
        <p className="dm-page__lede">
          Ask any denial, coding, payer-policy, or appeal question. Answers
          are sourced from the full denial-mitigation knowledge graph
          (coding manuals, payer policies, Medicare LCDs/NCDs, appeal
          procedures, modifier rules, ICD/CPT).
        </p>
        <div className="dm-page__actions">
          <a className="dm-btn dm-btn--secondary" href={openHref}
              target="_blank" rel="noreferrer">
            Open in new tab
          </a>
          <button type="button" className="dm-btn dm-btn--secondary"
                    onClick={reload}>
            Reload chat
          </button>
        </div>
      </header>

      {blocked && (
        <div className="dm-banner dm-banner--error">
          The embedded chat could not be loaded inline (likely a browser or
          network restriction).{' '}
          <a href={openHref} target="_blank" rel="noreferrer">
            Open it in a new tab
          </a>{' '}
          instead.
        </div>
      )}

      <div className="dm-askcm__frame-wrap">
        <iframe
          ref={iframeRef}
          src={src}
          title="CerebralMachines Test Graph chat"
          className="dm-askcm__frame"
          allow="clipboard-write; clipboard-read"
        />
      </div>

      <p className="dm-askcm__note">
        Tip: chat runs against the live denial-mitigation knowledge graph.
        Ask anything from "what modifiers bypass NCCI for CPT 64483?" to
        "summarise the FY2026 ICD-10-CM sepsis guidelines".
      </p>
    </div>
  );
}
